import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import { useAppSelector } from '../../redux/hooks.ts'
import { selectTheme } from '../../features/setting/setting.slice.ts'
import { Theme } from '../../features/setting/setting.def.ts'
import {
  darkAgGridTheme,
  lightAgGridTheme,
} from '../../styles/ag-grid/token.ts'
import {
  GetContextMenuItemsParams,
  MenuItemDef,
  ValueFormatterParams,
} from 'ag-grid-enterprise'
import { selectUser } from '../../features/user/user.slice.ts'

interface IdeyaAgGridTableProps extends AgGridReactProps {
  columns?: ColDef[]
  height?: number
  ref?: React.RefObject<AgGridReact | null>
}

export const IdeyaAgGridTable: React.FC<IdeyaAgGridTableProps> = ({
  columns,
  height = 700,
  ref,
  getContextMenuItems,
  ...agGridProps
}) => {
  const theme = useAppSelector(selectTheme)
  const userInfo = useAppSelector(selectUser)

  const defaultColDef: ColDef = {
    resizable: true,
  }

  const columnTypes = {
    numberColumn: {
      cellClass: 'ag-right-aligned-cell',
      headerClass: 'ag-right-aligned-header',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value
          ? params.value.toLocaleString(undefined, { maximumFractionDigits: 0 })
          : '0'
      },
    },
    textColumn: {
      cellClass: 'ag-left-aligned-cell',
      headerClass: 'ag-left-aligned-header',
    },
  }

  // Request from PPS team to not allow export table items for external users.
  // Currently if users have a userInfo.role, then they are internal users.
  const combinedGetContextMenuItems = (params: GetContextMenuItemsParams) => {
    let items: (string | MenuItemDef)[] = []

    if (typeof getContextMenuItems === 'function') {
      items = (getContextMenuItems(params) as MenuItemDef[]) ?? []
      if (userInfo.role) {
        const hasExport = items.some(
          (item) =>
            (typeof item === 'string' && item === 'export') ||
            (typeof item === 'object' && item.name === 'Export')
        )
        if (!hasExport) items.push('export')
      }
    } else if (params.defaultItems) {
      items = params.defaultItems
      if (!userInfo.role) {
        items = items.filter(
          (item) =>
            !(
              typeof item === 'string' &&
              (item === 'export' ||
                item === 'csvExport' ||
                item === 'excelExport')
            ) &&
            !(
              typeof item === 'object' &&
              (item.name === 'Export' ||
                item.name === 'CSV Export' ||
                item.name === 'Excel Export')
            )
        )
      }
    }

    return items as MenuItemDef[]
  }

  return (
    <div style={{ height }}>
      <AgGridReact
        ref={ref}
        theme={theme === Theme.Dark ? darkAgGridTheme : lightAgGridTheme}
        columnDefs={columns}
        rowModelType={'clientSide'}
        defaultColDef={defaultColDef}
        columnTypes={columnTypes}
        getContextMenuItems={combinedGetContextMenuItems}
        suppressDragLeaveHidesColumns={true}
        {...agGridProps}
      />
    </div>
  )
}
